import type { CSSProperties, HTMLAttributes } from 'react';

import { ModeBadge } from '../core/ModeBadge';
import type { IconName } from '../core/Icon';
import { useTranslation } from '../data/translations';

/** Mapping DayMode enum value → catalogue i18n key suffix.
 *  Les valeurs domaine (recovery, busy, travel, difficult) ne correspondent pas
 *  directement aux clés catalogue (recuperation, charge, voyage, difficile). */
const DAY_MODE_TO_CATALOG: Record<string, string> = {
  recovery: 'recuperation',
  busy: 'charge',
  travel: 'voyage',
  difficult: 'difficile',
};

function modeCatalogKey(dayMode: string): string {
  return `mode.${DAY_MODE_TO_CATALOG[dayMode] ?? dayMode}`;
}

export interface ContextStatusProps extends Omit<HTMLAttributes<HTMLDivElement>, 'style'> {
  readonly mode?: string | undefined;
  readonly modeIcon?: IconName;
  readonly state?: string | null;
  readonly stateIcon?: IconName | undefined;
  readonly size?: 'sm' | 'md';
  readonly style?: CSSProperties;
  /** Ouvre `ContextModeSelector` — la pastille MODE devient elle-même le contrôle interactif. */
  readonly onOpenMode?: () => void;
  readonly modeExpanded?: boolean;
  readonly modeControls?: string;
  readonly modePending?: boolean;
  /** Ouvre `EmotionalStateSelector` — la pastille ÉTAT devient elle-même le contrôle interactif. */
  readonly onOpenState?: () => void;
  readonly stateExpanded?: boolean;
  readonly stateControls?: string;
  readonly statePending?: boolean;
}

/**
 * Affiche le MODE et, séparément, l'ÉTAT lorsqu'il est actif.
 * Les deux axes ne sont jamais fondus en une seule pastille : le mode Normal
 * n'est pas l'état Joie, et un utilisateur peut n'avoir déclaré aucun état.
 *
 * Chaque pastille est elle-même le contrôle d'ouverture de son sélecteur (`onOpenMode`/
 * `onOpenState`) — il n'existe pas de bouton « Changer de mode »/« Déclarer un état » séparé,
 * qui dupliquerait la même action.
 */
export function ContextStatus({
  mode,
  modeIcon = 'focus',
  state = null,
  stateIcon = 'joie',
  size = 'md',
  style,
  onOpenMode,
  modeExpanded,
  modeControls,
  modePending,
  onOpenState,
  stateExpanded,
  stateControls,
  statePending,
  ...rest
}: ContextStatusProps) {
  const { t } = useTranslation();
  return (
    <div {...rest} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, ...style }}>
      {onOpenMode ? (
        <ModeBadge
          prefix={t('mode.label')}
          label={mode ? t(modeCatalogKey(mode)) : undefined}
          icon={modeIcon}
          size={size}
          onClick={onOpenMode}
          expanded={modeExpanded}
          controls={modeControls}
          pending={modePending}
          aria-label={`${t('mode.changer')} — ${mode ? t(modeCatalogKey(mode)) : ''}`}
        />
      ) : (
        <ModeBadge prefix={t('mode.label')} label={mode ? t(modeCatalogKey(mode)) : undefined} icon={modeIcon} size={size} />
      )}
      {state && (
        <>
          <span
            aria-hidden="true"
            style={{ width: 1, alignSelf: 'stretch', minHeight: 20, background: 'var(--accent-line)' }}
          />
          {onOpenState ? (
            <ModeBadge
              prefix={t('state.label')}
              label={t(`state.${state}`)}
              icon={stateIcon}
              size={size}
              onClick={onOpenState}
              expanded={stateExpanded}
              controls={stateControls}
              pending={statePending}
              aria-label={`${t('state.declarer')} — ${t(`state.${state}`)}`}
            />
          ) : (
            <ModeBadge prefix={t('state.label')} label={t(`state.${state}`)} icon={stateIcon} size={size} />
          )}
        </>
      )}
      {!state && onOpenState && (
        <>
          <span
            aria-hidden="true"
            style={{ width: 1, alignSelf: 'stretch', minHeight: 20, background: 'var(--accent-line)' }}
          />
          <ModeBadge
            prefix={t('state.label')}
            label={t('state.clear')}
            icon={stateIcon}
            size={size}
            onClick={onOpenState}
            expanded={stateExpanded}
            controls={stateControls}
            pending={statePending}
            aria-label={t('state.declarer')}
          />
        </>
      )}
    </div>
  );
}
